import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Building2 } from '@/components/icons/glyphs/Building2'; import { Check } from '@/components/icons/glyphs/Check'; import { ChevronsUpDown } from '@/components/icons/glyphs/ChevronsUpDown';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/hooks/use-session';
import { rememberTenant, useCurrentTenant } from '@/hooks/use-tenant';

/** Selector de tenant: el elegido vive en `?tenant=` (URL) y se recuerda en localStorage. */
export function TenantSwitcher() {
  const { tenant, tenants, isLoading } = useCurrentTenant();
  const { isPlatformAdmin } = useSession();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (isLoading) return <Skeleton className="h-9 w-48" />;

  const choose = (id: string) => {
    rememberTenant(id);
    // Rutas que dependen del tenant en la ruta misma se reubican; el resto solo cambia `?tenant=`.
    if (pathname.startsWith('/tenants/')) void navigate({ to: '/tenants/$tenantId', params: { tenantId: id } });
    // Una demo es de UN agente del tenant anterior: al cambiar de tenant se vuelve a la lista de Demos del nuevo.
    else if (pathname.startsWith('/demos/')) void navigate({ to: '/demos', search: { tenant: id } });
    else if (pathname.startsWith('/agents/')) void navigate({ to: '/agents', search: { tenant: id } });
    else
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({ ...prev, tenant: id, conversation: undefined }),
      });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="h-9 min-w-44 max-w-64 justify-between px-3"
          aria-label="Seleccionar tenant"
        >
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="truncate">{tenant ? tenant.name : 'Sin tenants'}</span>
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-72 overflow-y-auto">
        <DropdownMenuLabel>Tenants</DropdownMenuLabel>
        {tenants.length === 0 ? (
          <div className="px-2 py-3 text-sm text-muted-foreground">
            Aún no hay tenants. Crea el primero desde Tenants.
          </div>
        ) : (
          tenants.map((t) => (
            <DropdownMenuItem key={t.id} onSelect={() => choose(t.id)}>
              <span className="min-w-0 flex-1 truncate">{t.name}</span>
              {t.isDemo ? <Badge variant="primary">demo</Badge> : null}
              {tenant?.id === t.id ? <Check className="size-4 text-primary" aria-hidden /> : null}
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() =>
            void navigate(
              isPlatformAdmin
                ? { to: '/platform/tenants', search: { new: true } }
                : { to: '/tenants', search: { new: true } },
            )
          }
        >
          Crear tenant…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
